import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
export function WhatsAppTestimonialsSection() {
  const {
    lang
  } = useLanguage();
  const testimonials = [{
    text: '"Annysol transformó por completo mi presencia digital. En menos de 2 semanas tenía mi web, mi marca y mis redes listas para atraer clientes. El proceso fue claro, rápido y 100% personalizado."',
    textEn: '"Annysol completely transformed my digital presence. In less than 2 weeks I had my website, my brand, and my networks ready to attract clients. The process was clear, fast, and 100% personalized."',
    initial: 'M',
    name: 'María C.',
    role: 'E-commerce · Miami, FL'
  }, {
    text: '"Lo que más me sorprendió fue que no se trató de una plantilla. Annysol entendió mi negocio, mi mensaje y construyó algo que realmente me representa. Los clientes correctos ahora me encuentran."',
    textEn: '"What surprised me most was that it wasn\'t a template. Annysol understood my business, my message, and built something that truly represents me. The right clients now find me."',
    initial: 'C',
    name: 'Carlos R.',
    role: 'Consultor · Colombia',
    roleEn: 'Consultant · Colombia'
  }, {
    text: '"Tenía presencia pero no sistema. Inkova me ayudó a clarificar mi oferta, rediseñar mi web y activar mi primer funnel de captación. Los resultados llegaron en la primera semana."',
    textEn: '"I had a presence but no system. Inkova helped me clarify my offer, redesign my website, and activate my first capture funnel. The results arrived in the first week."',
    initial: 'L',
    name: 'Laura M.',
    role: 'Coach · Venezuela'
  }];
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [paused, setPaused] = useState(false);
  const count = testimonials.length;
  const goTo = useCallback((next: number, dir: number) => {
    setDirection(dir);
    setIndex((next % count + count) % count);
  }, [count]);
  const prev = useCallback(() => goTo(index - 1, -1), [goTo, index]);
  const next = useCallback(() => goTo(index + 1, 1), [goTo, index]);
  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => {
      setDirection(1);
      setIndex(i => (i + 1) % count);
    }, 5000);
    return () => clearInterval(id);
  }, [paused, count, index]);
  const active = testimonials[index];
  const role = lang === 'es' ? active.role : active.roleEn || active.role;
  const variants = {
    enter: (dir: number) => ({
      opacity: 0,
      x: dir > 0 ? 40 : -40
    }),
    center: {
      opacity: 1,
      x: 0
    },
    exit: (dir: number) => ({
      opacity: 0,
      x: dir > 0 ? -40 : 40
    })
  };
  return <section id="testimonios" className="py-16 lg:py-[100px] bg-[#FAF8F5] overflow-hidden" data-id="element-509">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-510">
        {/* ─────────────── HEADER ─────────────── */}
        <div className="text-center mb-12 lg:mb-16" data-id="element-511">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          ease: 'easeOut'
        }} className="flex flex-col items-center gap-4 mb-8" data-id="element-512">
            <span className="block w-[1px] h-[40px] bg-[#5A4579]/30" data-id="element-513" />
            <span className="inline-block font-sans text-[10px] font-bold tracking-[3px] uppercase text-[#5A4579]" data-id="element-514">
              {lang === 'es' ? 'LO QUE DICEN' : 'WHAT THEY SAY'}
            </span>
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          ease: 'easeOut',
          delay: 0.1
        }} className="font-display text-[19px] sm:text-[3.2vw] lg:text-[44px] text-[#1A1A2E] leading-[1.1] tracking-[-0.5px] mb-6 mx-auto whitespace-nowrap" data-id="element-515">
            {lang === 'es' ? <>
                Lo que dicen quienes ya dieron el{' '}
                <em className="italic text-[#5A4579]" data-id="element-516">paso</em>
              </> : <>
                What those who already took the{' '}
                <em className="italic text-[#5A4579]" data-id="element-517">step</em> say
              </>}
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          ease: 'easeOut',
          delay: 0.2
        }} className="text-[16px] lg:text-[18px] text-[#6B7280] font-light leading-[1.8]" data-id="element-518">
            {lang === 'es' ? 'Resultados reales de personas reales.' : 'Real results from real people.'}
          </motion.p>
        </div>

        {/* ─────────────── CAROUSEL ─────────────── */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: 'easeOut',
        delay: 0.2
      }} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} className="relative flex items-center justify-center gap-4 lg:gap-6" data-id="element-519">
          {/* Prev arrow — desktop only */}
          <button type="button" onClick={prev} aria-label={lang === 'es' ? 'Anterior' : 'Previous'} className="hidden lg:flex shrink-0 w-11 h-11 rounded-full items-center justify-center bg-white border border-[rgba(90,69,121,0.2)] shadow-[0_4px_12px_rgba(0,0,0,0.08)] text-[#5A4579] transition-colors duration-200 hover:bg-[#5A4579] hover:text-white" data-id="element-520">
            <ChevronLeft className="w-5 h-5" strokeWidth={1.75} data-id="element-521" />
          </button>

          {/* Card viewport with side peek */}
          <div className="relative w-full max-w-[620px] px-0 lg:px-[60px]" data-id="element-522">
            <AnimatePresence mode="wait" custom={direction} initial={false} data-id="element-523">
              <motion.div key={index} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.4,
              ease: 'easeInOut'
            }} drag="x" dragConstraints={{
              left: 0,
              right: 0
            }} dragElastic={0.2} onDragEnd={(_e, info) => {
              if (info.offset.x < -60) next();else if (info.offset.x > 60) prev();
            }} className="relative bg-white rounded-[24px] p-7 lg:p-10 border border-[rgba(90,69,121,0.08)] shadow-[0_8px_40px_rgba(90,69,121,0.12)] lg:min-h-[280px] flex flex-col text-center cursor-grab active:cursor-grabbing" data-id="element-524">
                {/* Decorative opening quote */}
                <span aria-hidden="true" className="pointer-events-none select-none absolute top-2 left-5 font-display text-[80px] leading-none text-[#5A4579]/[0.15]" data-id="element-525">
                  “
                </span>

                {/* Stars */}
                <div className="relative z-10 text-[#F59E0B] text-[18px] mb-5 tracking-[2px]" data-id="element-526">
                  ★★★★★
                </div>

                {/* Quote */}
                <p className="relative z-10 font-display italic text-[16px] sm:text-[17px] lg:text-[20px] text-[#1A1A2E] leading-[1.7] flex-1" data-id="element-527">
                  {lang === 'es' ? active.text : active.textEn}
                </p>

                {/* Divider */}
                <span className="block w-10 h-[2px] bg-[#5A4579] mx-auto my-6" data-id="element-528" />

                {/* Client info */}
                <div className="relative z-10 flex items-center justify-center gap-[14px]" data-id="element-529">
                  <div className="w-11 h-11 rounded-full bg-[linear-gradient(135deg,#5A4579,#A78BCA)] flex items-center justify-center text-white font-sans font-bold text-[16px] shrink-0" data-id="element-530">
                    {active.initial}
                  </div>
                  <div className="text-left" data-id="element-531">
                    <div className="text-[15px] font-bold text-[#1A1A2E]" data-id="element-532">
                      {active.name}
                    </div>
                    <div className="text-[12px] text-[#6B7280]" data-id="element-533">{role}</div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Next arrow — desktop only */}
          <button type="button" onClick={next} aria-label={lang === 'es' ? 'Siguiente' : 'Next'} className="hidden lg:flex shrink-0 w-11 h-11 rounded-full items-center justify-center bg-white border border-[rgba(90,69,121,0.2)] shadow-[0_4px_12px_rgba(0,0,0,0.08)] text-[#5A4579] transition-colors duration-200 hover:bg-[#5A4579] hover:text-white" data-id="element-534">
            <ChevronRight className="w-5 h-5" strokeWidth={1.75} data-id="element-535" />
          </button>
        </motion.div>

        {/* ─────────────── DOTS ─────────────── */}
        <div className="flex items-center justify-center gap-1.5 mt-8" data-id="element-536">
          {testimonials.map((_, i) => <button key={i} type="button" onClick={() => goTo(i, i > index ? 1 : -1)} aria-label={`${lang === 'es' ? 'Ir al testimonio' : 'Go to testimonial'} ${i + 1}`} className={`h-1.5 rounded-full transition-all duration-300 ease-in-out ${i === index ? 'w-6 bg-[#5A4579]' : 'w-1.5 bg-[rgba(90,69,121,0.2)] hover:bg-[rgba(90,69,121,0.4)]'}`} data-id="element-537" />)}
        </div>

        {/* ─────────────── GOOGLE LINK ─────────────── */}
        <div className="text-center mt-7" data-id="element-538">
          <a href="https://g.page/r/inkovacreation/review" target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-1.5 text-[#5A4579] text-[13px] font-medium hover:underline underline-offset-4 transition-colors duration-200" data-id="element-539">
            {lang === 'es' ? 'Ver todos los reviews en Google' : 'See all reviews on Google'}
            <span className="transition-transform duration-200 group-hover:translate-x-[3px]" data-id="element-540">
              →
            </span>
          </a>
        </div>
      </div>
    </section>;
}